/**
 * Rolling every issuer's sector vocabulary up to one set of names.
 *
 * NOT PUBLISHED DATA — the app's own, on the same terms as sectors.ts.
 *
 * Needed because a fund of funds publishes its breakdown in one taxonomy and
 * the funds inside it publish theirs in another. HYLD says 46% "Information
 * Technology"; the technology inside it sits in QMAX, which says "Technology".
 * HDIV says 38.3% "Financials"; the financials inside it sit in HMAX, which says
 * "Banks", "Insurance" and "Asset Management". Matching a clicked slice against
 * the holdings by string equality finds nothing in either case, which is exactly
 * the empty panel this fixes.
 *
 * Used for *matching only*. Nothing here is ever displayed — a slice keeps the
 * issuer's own words on screen, because that is what the issuer published and
 * what a reader will find on the fund page.
 *
 * The two entries that look like mistakes and are not: healthcare REITs are real
 * estate rather than healthcare, and telecom tower REITs are real estate rather
 * than communications. Both are landlords, and the tenant's business is not the
 * REIT's. GICS agrees. Pipelines are energy for the same reason — midstream is
 * not a utility, however utility-shaped the fund holding it looks.
 */

/**
 * Broad sectors, each with the labels that mean *the same thing* and the labels
 * that mean *something inside it*.
 *
 * The split is load-bearing. Clicking a broad slice should gather everything
 * beneath it — "Financials" on HDIV has to reach HMAX's banks and insurers, or
 * the panel is empty. Clicking a narrow slice must not widen — "Banks" on HMAX
 * answering with every financial would be a different question than the one
 * asked. Only `aliases` count as the broad form.
 */
const SECTORS: Record<string, { aliases: string[]; narrower: string[] }> = {
    technology: {
        aliases: ['Technology', 'Information Technology'],
        narrower: [
            'Software',
            'Software & Services',
            'Semiconductors',
            'Semiconductors & Semiconductor Equipment',
            'Semiconductors & Semiconductor',
            'Technology Hardware',
            'Technology Hardware, Storage & Peripherals',
            'Technology Hardware, Storage &',
            'Communications Equipment',
            'Electronic Equipment, Instruments & Components',
        ],
    },
    financials: {
        aliases: ['Financials'],
        narrower: ['Banks', 'US Banks', 'Insurance', 'Asset Management', 'Asset Mgmt/Invt Bank & Brokers', 'Capital Markets', 'Other Financials', 'Deposit Taking / Lending'],
    },
    healthcare: {
        aliases: ['Healthcare', 'Health Care'],
        narrower: ['Biotechnology', 'Pharmaceuticals', 'Health Care Equipment & Supplies', 'Health Care Providers & Services', 'Life Sciences Tools & Services'],
    },
    'communication services': {
        aliases: ['Communication Services', 'Communications', 'Telecoms'],
        narrower: ['Media', 'Media & Entertainment', 'Interactive Media & Services', 'Diversified Telecommunication Services', 'Wireless Telecommunication Services'],
    },
    'consumer discretionary': {
        aliases: ['Consumer Discretionary'],
        narrower: ['Automobiles & Components', 'Casinos & Gaming', 'Hotels, Resorts & Cruise Lines', 'Leisure Facilities'],
    },
    'consumer staples': { aliases: ['Consumer Staples'], narrower: [] },
    industrials: {
        aliases: ['Industrials'],
        narrower: [
            'Aerospace & Defense',
            'Agricultural & Farm Machinery',
            'Air Freight & Logistics',
            'Construction Machinery & Heavy Transportation Equipment',
            'Electrical Components & Equipment',
            'Environmental & Facilities Services',
            'Heavy Electrical Equipment',
            'Industrial Machinery & Supplies & Components',
            'Passenger Airlines',
            'Passenger Ground Transportation',
            'Rail Transportation',
            'Trading Companies & Distributors',
        ],
    },
    materials: { aliases: ['Materials'], narrower: ['Gold', 'Silver'] },
    energy: {
        aliases: ['Energy'],
        narrower: [
            'Pipelines',
            'Integrated Oil & Gas',
            'Oil & Gas Equipment & Services',
            'Oil & Gas Exploration & Production',
            'Oil & Gas Refining & Marketing',
            'Oil & Gas Storage & Transportation',
            'Oil, Gas & Consumable Fuels',
        ],
    },
    'real estate': {
        aliases: ['Real Estate', 'REITs'],
        narrower: [
            'Real Estate Services',
            'Real Estate Operating Companies',
            'Data Center REITs',
            'Diversified REITs',
            'Health Care REITs',
            'Healthcare REITs',
            'Hotel & Resort REITs',
            'Industrial REITs',
            'Multi-Family Residential REITs',
            'Office REITs',
            'Other Specialized REITs',
            'Retail REITs',
            'Self-Storage REITs',
            'Single-Family Residential REIT',
            'Telecom Tower REITs',
        ],
    },
    utilities: {
        aliases: ['Utilities'],
        narrower: [
            'Electric Utilities',
            'Gas Utilities',
            'Multi-Utilities',
            'Water Utilities',
            'Renewable Power Generation',
            'Battery & Energy Storage Equipment',
            'Hydrogen & Fuel Cell Equipment',
            'Solar Equipment & Services',
            'Wind Equipment & Services',
            'Other Equipment & Services',
        ],
    },
};

/**
 * Published slices that are deliberately not sectors.
 *
 * A fund-of-funds bucket ("Diversified"), a residual ("Other"), an asset class
 * ("Government Bonds") and a size band ("Large Cap") are all real published
 * slices and none of them says what business anything is in. Listed rather than
 * left to fall through, so "we have not classified this" and "this cannot be
 * classified" stay different answers.
 */
const NOT_SECTORS = new Set(
    [
        'Diversified',
        'Other',
        'Equities',
        'Fixed Income',
        'Cash Equivalent',
        'Cash Equivalents',
        'Government Bonds',
        'Treasury Bonds',
        'Long-Term',
        'Short-Term',
        'Large Cap',
        'Bitcoin Holding',
        'Bitcoin Miners',
        'Bitcoin Trading & Services',
        'Crypto Miners',
        'Crypto Trading/ Wallets',
        'Digital Assets & Services',
        'Transaction Processing/ Staking',
    ].map((s) => s.trim().toLowerCase()),
);

const normalize = (label: string): string => label.trim().toLowerCase().replace(/\s+/g, ' ');

const CANON = new Map<string, string>();
const BROAD = new Set<string>();
for (const [canonical, { aliases, narrower }] of Object.entries(SECTORS)) {
    for (const label of aliases) {
        CANON.set(normalize(label), canonical);
        BROAD.add(normalize(label));
    }
    for (const label of narrower) CANON.set(normalize(label), canonical);
}

/** The broad sector this label rolls up to, or null where it is unmapped or is
    not a sector at all. */
export const canonicalSector = (label: string | null): string | null => {
    if (label == null) return null;
    const key = normalize(label);
    return NOT_SECTORS.has(key) ? null : (CANON.get(key) ?? null);
};

/** A published slice that names no line of business, so nothing can be in it. */
export const isNonSector = (label: string): boolean => NOT_SECTORS.has(normalize(label));

/** How a slice was matched, which the panel says out loud — a rolled-up match
    is an inference and should not read as the issuer's own figure. */
export type MatchKind = 'exact' | 'rolled' | 'not-a-sector' | 'none';

/**
 * The holdings belonging to a published slice.
 *
 * Three tiers, in order:
 *
 *   broad slice   roll up, so "Financials" reaches banks and insurers
 *   narrow slice  exact only, so "Banks" is not answered with every financial
 *   narrow, empty roll up anyway — a wider answer beats a blank panel, and the
 *                 weight shown against the published one makes it obvious
 *
 * Generic over the item so it can be checked without a fund or a component.
 */
export const matchSector = <T>(slice: string, items: readonly T[], sectorOf: (item: T) => string | null): { items: T[]; how: MatchKind } => {
    if (isNonSector(slice)) return { items: [], how: 'not-a-sector' };

    const exact = items.filter((item) => {
        const label = sectorOf(item);
        return label != null && normalize(label) === normalize(slice);
    });

    if (!BROAD.has(normalize(slice)) && exact.length > 0) return { items: exact, how: 'exact' };

    const canonical = canonicalSector(slice);
    if (canonical == null) return exact.length > 0 ? { items: exact, how: 'exact' } : { items: [], how: 'none' };

    const rolled = items.filter((item) => canonicalSector(sectorOf(item)) === canonical);
    if (rolled.length === 0) return { items: exact, how: exact.length > 0 ? 'exact' : 'none' };

    // Same set either way means the roll-up added nothing, so call it exact —
    // the panel should only warn about inference where inference happened.
    return { items: rolled, how: rolled.length === exact.length ? 'exact' : 'rolled' };
};
